import { motion } from 'framer-motion'
import { ChevronDown, Heart, Target, Eye, Users, BookOpen, Globe, Church } from 'lucide-react'
import { beliefs } from '../../data/seedData'
import { Section, Button } from '../../components/common'
import { useState } from 'react'
import { Link } from 'react-router-dom'

const BeliefAccordion = ({ belief, isOpen, onToggle }) => (
  <motion.div
    className="border border-gray-200 rounded-xl overflow-hidden"
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
    >
      <span className="font-heading font-semibold text-lg text-dark-100">{belief.title}</span>
      <ChevronDown
        className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0 }}
      className="overflow-hidden"
    >
      <div className="p-6 pt-0 bg-white">
        <p className="text-gray-600 mb-3">{belief.description}</p>
        <p className="text-sm text-primary-600 font-medium">
          <BookOpen className="w-4 h-4 inline mr-2" />
          {belief.scripture}
        </p>
      </div>
    </motion.div>
  </motion.div>
)

const About = () => {
  const [openBelief, setOpenBelief] = useState(0)

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1920"
            alt="Church"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-dark via-dark/90 to-dark/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary-400 font-accent font-semibold uppercase tracking-wider">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mt-4 mb-6">
              Who We Are
            </h1>
            <p className="text-xl text-gray-300">
              A multinational, Bible-centered holiness community committed to spreading the Gospel of Jesus Christ to all nations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <Section>
        <Section.Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-secondary-600 font-accent font-semibold uppercase tracking-wider">
                About Us
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mt-2 mb-6">
                Deeper Life Bible Church
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                The Deeper Life Bible Church represents a multinational, Bible-centered holiness
                organization committed to spreading the Gospel of Jesus Christ to all nations.
              </p>
              <p className="text-gray-600 mb-4">
                Founded in 1973 in Lagos, Nigeria with just 15 initial members, the church has
                expanded dramatically under the guidance of its founder and General Superintendent,
                Pastor Dr. William F. Kumuyi. The organization now encompasses millions of adherents
                with a presence spanning Africa, Europe, Asia, the Middle East, and more than 90
                branches throughout the United States.
              </p>
              <p className="text-gray-600">
                Our Clarksville branch continues this mission, serving as a citadel of pursuit and
                thirst after God. We are a people constantly seeking to become more like Jesus by
                beholding His glory through genuine worship, unadulterated teachings, and a steadfast
                commitment to follow His lead in sharing the message of God's love to all humanity.
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-primary-50 rounded-2xl p-6 text-center">
                <Globe className="w-8 h-8 text-primary-600 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-dark-100 mb-2">Global Presence</h3>
                <p className="text-sm text-gray-600">Spanning Africa, Europe, Asia, and 90+ branches in the USA</p>
              </div>
              <div className="bg-secondary-50 rounded-2xl p-6 text-center">
                <Users className="w-8 h-8 text-secondary-600 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-dark-100 mb-2">Founded 1973</h3>
                <p className="text-sm text-gray-600">Started with 15 members under Pastor Dr. William F. Kumuyi</p>
              </div>
              <div className="bg-accent-50 rounded-2xl p-6 text-center">
                <Heart className="w-8 h-8 text-accent-600 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-dark-100 mb-2">Our Mission</h3>
                <p className="text-sm text-gray-600">Disciple A Whole Neighborhood (DAWN) - one family at a time</p>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-6 text-center">
                <Church className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-dark-100 mb-2">Holiness</h3>
                <p className="text-sm text-gray-600">Committed to holiness and unadulterated biblical teaching</p>
              </div>
            </motion.div>
          </div>
        </Section.Container>
      </Section>

      {/* Vision & Mission Section */}
      <Section className="bg-gradient-to-br from-primary-900 to-secondary-900 text-white">
        <Section.Container>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Eye className="w-12 h-12 text-accent-400 mb-4" />
              <h3 className="text-2xl font-heading font-bold mb-4">Our Vision</h3>
              <p className="text-gray-200">
                To make Christ Jesus known to everyone in every county in the State of Tennessee,
                USA. This is consistent with the global vision of our international headquarters — to
                share the good news of God's love and grace to every person of every race, nation, and
                social class.
              </p>
            </motion.div>
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Target className="w-12 h-12 text-accent-400 mb-4" />
              <h3 className="text-2xl font-heading font-bold mb-4">Our Mission</h3>
              <p className="text-gray-200">
                To Disciple A Whole Neighborhood (DAWN) one family at a time, through genuine worship,
                unadulterated teachings from God's Word, and a steadfast commitment to follow Christ's
                lead in sharing the message of God's love to all humanity.
              </p>
            </motion.div>
          </div>
        </Section.Container>
      </Section>

      {/* Gospel to Every Creature - Dr. Kumuyi Section */}
      <Section className="bg-white overflow-hidden">
        <Section.Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary-500 font-accent font-semibold uppercase tracking-wider">
                MARK 16:15
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-dark-100 mt-3 mb-6 italic">
                The Gospel to Every Creature
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mb-6" />
              <p className="text-gray-600 leading-relaxed mb-8">
                This was the mission and mandate of the Lord Jesus Christ to the world two
                thousand years ago. It is this same mandate and mission that now drives me
                and has led to the birth of the GCK, proclaiming the gospel, and bringing
                reprieve and solace to a world gripped with mounting and seemingly
                unresolvable crises.
              </p>

              {/* Ministry Images */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <img
                    src="/images/gck-ministry-1.png"
                    alt="Global Crusade with Kumuyi"
                    className="w-full h-40 object-cover"
                  />
                </motion.div>
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <img
                    src="/images/gck-ministry-2.png"
                    alt="Dr. Kumuyi Preaching"
                    className="w-full h-40 object-cover"
                  />
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Dr. Kumuyi Image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Background decoration */}
              <div className="absolute -top-8 -right-8 w-72 h-72 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full blur-3xl opacity-50" />

              <div className="relative">
                <img
                  src="/images/dr-kumuyi-headshot.png"
                  alt="Dr. W.F. Kumuyi"
                  className="w-full max-w-md mx-auto"
                />

                {/* Name Badge */}
                <motion.div
                  className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-xl shadow-xl"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-center">
                    Dr. W.F. Kumuyi
                  </h3>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </Section.Container>
      </Section>

      {/* Statement of Faith */}
      <Section className="bg-gradient-to-br from-primary-50 to-secondary-50">
        <Section.Container size="narrow">
          <Section.Header
            subtitle="What We Believe"
            title="Statement of Faith"
            description="Our beliefs are rooted in the inspired, infallible Word of God."
          />

          <div className="space-y-4">
            {beliefs.map((belief, index) => (
              <BeliefAccordion
                key={belief.title}
                belief={belief}
                isOpen={openBelief === index}
                onToggle={() => setOpenBelief(openBelief === index ? -1 : index)}
              />
            ))}
          </div>
        </Section.Container>
      </Section>

      {/* Join Us CTA */}
      <Section className="bg-gradient-to-r from-primary-900 to-secondary-900 text-white">
        <Section.Container>
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                Join Our Family
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                We invite you to join us for any of our service days. We would be honored to have
                you become a member of our One Big Happy Family.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/plan-your-visit">
                  <Button variant="accent" size="lg">
                    Plan Your Visit
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outlineWhite" size="lg">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </Section.Container>
      </Section>
    </>
  )
}

export default About
